import L from "leaflet"
import { BAddLoopCommand, BMoveVertexCommand, BRemoveItemCommand, BShapeChangeCommand } from "./commands/commands";
import { generateNodeIcon } from "./model/BNode";
import { addToMapIf, createHoleButton, createRemoveButton, get_sender_marker, getEditorColor, inEachArray, is_near_zero, is_open, is_outer } from "./ControllerUtils";
import { B_AttrFlags } from "./model/BBase";
import { simple_rect_coordinates } from "./model/BProject";

const UPDATE_ALL = 0;
const SUPPRESS_FEATURE_UPDATE = 2;

const CircleHandleIcon = L.icon({
	iconUrl: "/images/circle-handle-m.png",
	iconSize: [8, 8],
	iconAnchor: [4, 4],
	className: "sk-editor-vertex-handle"
});

const AddHandleIcon = L.icon({
	iconUrl: "/images/addh-m.png",
	iconSize: [8, 8],
	iconAnchor: [4, 4],
	className: "sk-editor-add-handle"
});

const ElevatorIcon = L.icon({
	iconUrl: "/images/ev.png",
	iconSize: [16, 16],
	iconAnchor: [3, 3],
	className: "sb-node-marker"
});

const StairIcon = L.icon({
	iconUrl: "/images/stairh.png",
	iconSize: [16, 16],
	iconAnchor: [3.5, 3.5],
	className: "sb-node-marker"
});

function clearOldFloorFeatures(lmap) {
	if (!lmap) { return; }

	lmap.eachLayer(lyr => {
		if (lyr._is_floor_object) {
			lyr.remove();
		}
	});
}

function ensureFloorMapFeatures(lmap, floor) {
	if (!lmap) { return; }
	if (!floor) { return; }

	floor.forEach( ch => {
		ensurePolygonMapFeatures(lmap, ch);
		ensurePolygonEditorFeatures(lmap, ch);

		ensurePointMapFeatures(lmap, ch)
	} );
}

function ensurePointMapFeatures(lmap, maybe_point_item) {
	if (!maybe_point_item.mapFeautre) {
		if (maybePoint(maybe_point_item)) {
			const iconInfo = {
				iconUrl: generateNodeIcon(16, 16, window.devicePixelRatio),
				iconSize: [16, 16],
				iconAnchor: [8, 8],
				className: "sb-node-marker"
			};

			const ft = L.marker(maybe_point_item.getSingleCoord(), 
				{
					icon: (maybe_point_item.flags & B_AttrFlags.ElevatorNode) ? ElevatorIcon : L.icon(iconInfo),
					draggable: true
				}
			);

			ft._is_floor_object = true;
			ft._owner_bitem = maybe_point_item;
			maybe_point_item.mapFeautre = ft;

			bindNodePopup(ft);
			ft.on("dragstart", onNodeDragStart);
			ft.on("move", onNodeMove);
			ft.on("dragend", onNodeDragEnd);

			maybe_point_item.onCoodinateChange = onNodeCoodinateChange;
		}
	}

	addToMapIf(lmap, maybe_point_item.mapFeautre);
}

function ensurePolygonMapFeatures(lmap, maybe_polygon_item) {
	if (!maybe_polygon_item.mapFeautre) {
		if (maybePolygon(maybe_polygon_item)) {
			const polygonColor = getEditorColor(maybe_polygon_item);
			const isO = is_outer(maybe_polygon_item);
			const isClosed = 0 === (maybe_polygon_item.flags & B_AttrFlags.OpenRing);
			const wt = (isO || !isClosed) ? 3 : 1;

			const ctor = isClosed ? L.polygon : L.polyline;
			const ft = ctor(maybe_polygon_item.coordinates, {
				color: polygonColor,
				weight: wt
			});
			ft._is_floor_object = true;
			maybe_polygon_item.mapFeautre = ft;

			if (maybe_polygon_item.flags & B_AttrFlags.Stairs) {
				ft._is_stair = true;
			}

			bindPolygonPopup(ft, maybe_polygon_item);
		}
	}

	addToMapIf(lmap, maybe_polygon_item.mapFeautre);
}

function ensurePolygonEditorFeatures(lmap, b_item) {
	if (b_item.requireVertexHandle) {
		if (!b_item.editorFeatures) {
			const vls = [];
			let als = null;
			b_item.editorFeatures = {
				v_handles: vls,
				map: lmap
			};

			if (0 === (b_item.flags & B_AttrFlags.OpenRing)) {
				als = [];
				b_item.editorFeatures.a_handles = als;
			}

			const coords = b_item.coordinates;
			const nLoops = coords.length;
			for (let lpi = 0;lpi < nLoops;++lpi) {
				const loop = coords[lpi];
				const n = loop.length;
				for (let i = 0;i < n;++i) {
					const cd = loop[i];
					const next_cd = loop[(i+1) % n];

					const vh = generateVertexHandle(lmap, b_item, cd, lpi, i);
					vls.push(vh);

					if (als) {
						const ah = generateAddHandle(lmap, b_item, cd, next_cd, lpi, i)
						als.push(ah);
					}
				}
			}
		
			b_item.onCoodinateChange = onCoodinateChange;
			b_item.onLengthChange = onLengthChange;
		} else {
			inEachArray(b_item.editorFeatures, a => {
				a.forEach( f => f.addTo(lmap) );
			});
		}
	}
}

function generateVertexHandle(lmap, b_item, cd, loopIndex, vIndex) {
	let icon = CircleHandleIcon;
	if ((b_item.flags & B_AttrFlags.Stairs) && vIndex === 0) {
		icon = StairIcon;
	}


	const h = generateHandleMarker(b_item, cd, icon, 1, "Drag to move this vertex").addTo(lmap);
	h._v_index = vIndex;
	h._loop_index = loopIndex;
	h.on("move", onHandleMove);
	h.on("dragstart", onHandleDragStart);
	h.on("dragend", onHandleDragEnd);

	if (!is_open(b_item)) {
		bindVertexPopup(h);
	}

	return h;
}

function generateAddHandle(lmap, b_item, cd, next_cd, loopIndex, vIndex) {
	const h = generateHandleMarker(b_item, mid_pos(cd, next_cd), AddHandleIcon, 1, "Drag to add a new vertex").addTo(lmap);
	h._v_index = vIndex;
	h._loop_index = loopIndex;
	h._is_add_handle = true;
	h._bound_vertex = null;
	h.on("move", onHandleMove);
	h.on("dragstart", onHandleDragStart);
	h.on("dragend", onHandleDragEnd);

	return h;
}

function mid_pos(a, b) {
	return [ (a[0]+b[0])/2 , (a[1]+b[1])/2 ];
}

function generateHandleMarker(b_item, coordinate, icon, opacity, title) {
	const mk = L.marker(coordinate, {
		icon: icon,
		draggable: true,
		opacity: opacity ?? 1,
		title: title
	});
	mk._owner_bitem = b_item;
	mk._is_floor_object = true;

	return mk;
}

function maybePolygon(item) {
	if (item.geometryType) {
		return item.geometryType.indexOf("g") > 0;
	}

	return false;
}

function maybePoint(item) {
	if (item.geometryType) {
		return item.geometryType.indexOf("t") > 0;
	}

	return false;
}

function disposeAllMapFeatures(proj) {
	proj.eachBuilding( building => {
		building.forEach( floor => {
			floor.forEach( item => {
				disposeFeatureIf(item);
			} );
		});
	});

}

function disposeFeatureIf(item) {
	if (item.mapFeautre) {
		item.mapFeautre.remove();
		item.mapFeautre = null;
	}

	disposeEditorFeatures(item);
}

function disposeEditorFeatures(item) {
	if (item.editorFeatures) {
		inEachArray(item.editorFeatures, innerArray => {
			for (const f of innerArray) {
				f.off();
				f.remove();
			}
		});

		delete item.editorFeatures.map;
		item.editorFeatures = null;
	}
}

// Node

function onNodeDragStart(e) {
	const sender_marker = get_sender_marker(e);
	if (!sender_marker?._owner_bitem) { return; }

	sender_marker._owner_bitem.saveCoordinates();
}

function onNodeMove(e) {
	const sender_marker = get_sender_marker(e);
	sender_marker?._owner_bitem?.modifyCoordinate(0,0, e.latlng.lng, e.latlng.lat, SUPPRESS_FEATURE_UPDATE);
}

function onNodeDragEnd(e) {
	addMoveCommandUndo(get_sender_marker(e), 0);
}

function onNodeCoodinateChange(sender, _loopIndex, _index, userData) {
	if (userData === SUPPRESS_FEATURE_UPDATE) { return; }

	if (sender.mapFeautre?.setLatLng) {
		const newCd = sender.getSingleCoord();
	
		const cur = sender.mapFeautre.getLatLng();
		const dx = newCd[0] - cur.lat;
		const dy = newCd[1] - cur.lng;

		if (!is_near_zero(dx) || !is_near_zero(dy)) {
			sender.mapFeautre.setLatLng(newCd);
		}
	}
}

// Polyline/polygon

function onHandleDragStart(e) {
	const sender_marker = get_sender_marker(e);
	if (!sender_marker) { return; }

	sender_marker._owner_bitem.saveCoordinates();

	if (!Number.isSafeInteger(sender_marker._bound_vertex)) {
		if (sender_marker._is_add_handle) {
			const [loopIndex, vIndex] = getHandleIndex(sender_marker);
			if (vIndex >= 0) {
				sender_marker._bound_vertex = sender_marker._owner_bitem.splitEdge(loopIndex, vIndex);
				sender_marker.setOpacity(0);
			}
		}
	}
}

function onHandleDragEnd(e) {
	const sender_marker = get_sender_marker(e);
	if (!sender_marker) { return; }
	
	if (Number.isSafeInteger(sender_marker._bound_vertex)) {
		renewEditorFeatures(sender_marker._owner_bitem);
	}

	if (sender_marker._is_add_handle) {
		// Add
		addShapeChangeCommandUndo(sender_marker);
	} else {
		// Move
		addMoveCommandUndo(sender_marker);
	}
}

function onHandleMove(e) {
	const sender_marker = get_sender_marker(e);
	if (!sender_marker) { return; }

	if (sender_marker._is_add_handle) {
		// Add handle
		if (Number.isSafeInteger(sender_marker._bound_vertex)) { 
			sender_marker._owner_bitem.modifyCoordinate(sender_marker._loop_index, sender_marker._bound_vertex, e.latlng.lng, e.latlng.lat, true);
		}
	} else {
		// Move handle
		const [loopIndex, vIndex] = getHandleIndex(sender_marker);
		if (vIndex >= 0 && sender_marker._owner_bitem.modifyCoordinate) {
			sender_marker._owner_bitem.modifyCoordinate(loopIndex, vIndex, e.latlng.lng, e.latlng.lat);
		}
	}
}

function renewEditorFeatures(sender_bitem) {
	const lmap = sender_bitem.editorFeatures?.map;
	if (lmap) {
		disposeEditorFeatures(sender_bitem);
		ensurePolygonEditorFeatures(lmap, sender_bitem);
	}
}

function getHandleIndex(handleMarker) {
	if (Number.isSafeInteger(handleMarker._v_index) && Number.isSafeInteger(handleMarker._loop_index)) {
		return [handleMarker._loop_index, handleMarker._v_index];
	}

	return [-1, -1];
}

function onCoodinateChange(sender, loopIndex, _index, suppressAdjustment) {
	updateMapFeatureLatLngs(sender);

	if (!suppressAdjustment) {
		if (Array.isArray(sender.editorFeatures?.a_handles)) {
			adjustAddHandlePositions(sender.editorFeatures.a_handles, sender.coordinates, loopIndex);
		}

		if (suppressAdjustment === UPDATE_ALL) {
			if (Array.isArray(sender.editorFeatures?.v_handles)) {
				adjustMoveHandlePositions(sender.editorFeatures.v_handles, sender.coordinates, loopIndex);
			}
		}
	}
}

function updateMapFeatureLatLngs(bitem) {
	if (bitem.mapFeautre?.setLatLngs) {
		bitem.mapFeautre.setLatLngs(bitem.coordinates);
	}
}

function onLengthChange(sender) {
	renewEditorFeatures(sender);
}

function adjustAddHandlePositions(a_handles, coordinates, _loopIndex) {
	a_handles.forEach( h => {
		const loop = coordinates[ h._loop_index ];
		const n = loop.length;
		const c1 = loop[ h._v_index];
		const c2 = loop[ (h._v_index+1) % n ];

		h.setLatLng(mid_pos(c1, c2));
	});
}

function adjustMoveHandlePositions(m_handles, coordinates, _loopIndex) {
	m_handles.forEach( h => {
		h.setLatLng(coordinates[h._loop_index][h._v_index]);
	});
}

function bindNodePopup(marker) {
	marker.bindPopup(
		generateNodePopup, {
			closeButton: false,
			className: "sb-vertex-popup",
			offset: [0, -2]
		}
	);
}

function generateNodePopup(sender) {
	const bitem = sender._owner_bitem;
	if (!bitem) { return ""; }

	const el = createRemoveButton();
	el.onclick = () => {
		const cmd = makeRemoveItemCommand(bitem);
		bitem.requestCommandExec(cmd);
		sender.closePopup();
	};

	return el;
}

function bindVertexPopup(v_handle) {
	v_handle.bindPopup( generateVertexPopup, {
		closeButton: false,
		className: "sb-vertex-popup"
	} );
}

function generateVertexPopup(sender) {
	const bitem = sender._owner_bitem;

	const [loopIndex, vIndex] = getHandleIndex(sender);
	let el = "Triangle vertices cannot be deleted.";
	if (bitem.getLengthOfLoop(loopIndex) > 3) {
		el = createRemoveButton();
		el.onclick = () => {
			sender.closePopup();

			const cmd = makeRemoveVertexCommand(bitem, loopIndex, vIndex);			
			bitem.requestCommandExec(cmd);
		};
	}

	return el;
}

function bindPolygonPopup(polygon, bitem) {
	polygon.bindPopup( generatePolygonPopup.bind(bitem), {
		closeButton: false,
		className: "sb-polygon-popup"
	} );

	polygon.on("popupopen", _e => set_data_active(polygon, 1) );
	polygon.on("popupclose", _e => set_data_active(polygon, 0) );
}

function generatePolygonPopup(sender) {
	const container = document.createElement("div");

	if (!is_open(this)) {
		const holeBtn = createHoleButton();

		holeBtn.onclick = () => {
			sender.closePopup();

			const loop_cmd = makeAddLoopCommand(this);
			this.requestCommandExec(loop_cmd);
		};
		container.appendChild(holeBtn);
	}

	if (0 === (this.flags & B_AttrFlags.BuildingOuter)) {
		const rmBtn = createRemoveButton();
		rmBtn.onclick = () => {
			sender.closePopup();

			const cmd = makeRemoveItemCommand(this);
			this.requestCommandExec(cmd);
		};

		container.appendChild(rmBtn);
	}

	return container;
}

function renderSpecialPolylineIf(polylineLayer) {
	if (polylineLayer._is_stair) {
		const path = polylineLayer._path;
		path.setAttribute("data-isstairs", 1);
	}
}

function makeAddLoopCommand(bitem) {
	// after the last
	const pos = bitem.countLoops();

	const cmd = new BAddLoopCommand({
		id: bitem.id,
		beforeThisIndex: pos,
		new_loop: simple_rect_coordinates(10)
	});
	return cmd;
}

function makeRemoveVertexCommand(bitem, loopIndex, vIndex) {
	bitem.saveCoordinates();

	if (!Number.isSafeInteger(loopIndex)) { throw new TypeError("loopIndex should be a integer"); }
	if (!Number.isSafeInteger(vIndex)) { throw new TypeError("vIndex should be a integer"); }

	const op = {
		id: bitem.id,
		loopIndex: loopIndex,
		old_coords: deep_copy_coords( bitem.prevCoordinates[loopIndex] ),
		new_coords: deep_copy_coords( bitem.coordinates[loopIndex] )
	};
	op.new_coords.splice(vIndex, 1);

	return new BShapeChangeCommand(op);
}

function makeRemoveItemCommand(item) {
	const r_cmd = new BRemoveItemCommand(item.serialize());
	return r_cmd;
}

function set_data_active(leaflet_layer, val) {
	const el = leaflet_layer?.getElement?.();
	if (el) {
		el.dataset.active = val;
	}
}

function addShapeChangeCommandUndo(sender_marker) {
	const bitem = sender_marker._owner_bitem;
	if (!bitem) { return; }

	const loopIndex = sender_marker._loop_index;
	const cmd = new BShapeChangeCommand({
		id: bitem.id,
		loopIndex: loopIndex,
		old_coords: deep_copy_coords( bitem.prevCoordinates[loopIndex] ),
		new_coords: deep_copy_coords( bitem.coordinates[loopIndex] )
	});

	bitem.requestUpdateUndo(cmd);
}

function addMoveCommandUndo(sender_marker, vIndex) {
	const bitem = sender_marker?._owner_bitem;
	if (!bitem) { return; }

	let loopIndex = 0;
	if (!Number.isSafeInteger(vIndex)) {
		[loopIndex, vIndex] = getHandleIndex(sender_marker);
	}

	const cmd = new BMoveVertexCommand({
		id: bitem.id,
		index: vIndex,
		loopIndex: loopIndex,
		old_pos: Array.from(bitem.getPrevCoordinateAt(loopIndex, vIndex)),
		new_pos: Array.from(bitem.getCoordinateAt(loopIndex, vIndex))
	});

	bitem.requestUpdateUndo(cmd);
}

function deep_copy_coords(coords) {
	if (!Array.isArray(coords)) { return coords; }
	return coords.map(c => deep_copy_coords(c));
}

function assert_nested_coords(outer) {
	let ok = false;
	if (Array.isArray(outer)) {
		if (outer.length > 0) {
			const firstLoop = outer[0];
			if (Array.isArray(firstLoop)) {
				if (firstLoop.length > 0) {
					ok = Array.isArray(firstLoop[0]) && Number.isFinite(firstLoop[0][0]) && Number.isFinite(firstLoop[0][1]);
				}
			}
		}
	}

	if (!ok) {
		console.log(outer);
		throw new Error("[Assertion failed] Coordinates should be nested");
	}
}

export { disposeAllMapFeatures, updateMapFeatureLatLngs, renewEditorFeatures, clearOldFloorFeatures, ensureFloorMapFeatures, mid_pos, UPDATE_ALL, deep_copy_coords, assert_nested_coords, renderSpecialPolylineIf };